import { Info, Loader2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { DEFAULT_PROMPT } from "@/config/presets";
import { PromptPresets } from "@/features/ai/PromptPresets";
import type { Slide } from "@/lib/types";

type Model = "gemini-2.5-flash" | "gemini-2.5-pro";

export type AiEditResult = {
  new_slide: Slide;
  ai_summary: string;
  operations_count: number;
  planned_count: number;
};

type Props = {
  slide: Slide;
  onApply: (result: AiEditResult) => void;
  onClose: () => void;
};

export function AiDrawer({ slide, onApply, onClose }: Props) {
  const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);
  const [model, setModel] = useState<Model>("gemini-2.5-flash");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AiEditResult | null>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/ai/edit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slide, instruction: prompt, model }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as AiEditResult;
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        className="fixed inset-0 z-20 bg-[var(--color-ink)]/40 backdrop-blur-[2px]"
      />
      <aside className="fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col overflow-auto border-l border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-lg)] sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-medium">AI に編集してもらう</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="flex h-10 w-10 items-center justify-center rounded-[10px] text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 flex items-start gap-2 rounded-[10px] bg-[var(--color-surface-muted)] p-3 text-xs leading-relaxed text-[var(--color-ink-muted)]">
          <Info size={14} className="mt-0.5 shrink-0 text-[var(--color-coral)]" />
          <div>
            このスライドの要約（shape 一覧＋テキスト・色・サイズ）だけを AI に送り、 「どの shape
            をどう変えるか」を operation の形で返してもらいます。 実際の JSON
            はサーバ側で確定的に書き換えるので、ID やレイアウト参照は
            壊れません。入力・出力とも短いので高速・低コストです。
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
            モデル
          </div>
          <div className="flex gap-2">
            {(["gemini-2.5-flash", "gemini-2.5-pro"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setModel(m)}
                className={
                  m === model
                    ? "rounded-[10px] bg-[var(--color-coral)] px-3 py-1 text-xs text-white"
                    : "rounded-[10px] border border-[var(--color-border)] px-3 py-1 text-xs"
                }
              >
                {m.replace("gemini-2.5-", "")}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
            プリセット（クリックで指示欄に入ります）
          </div>
          <PromptPresets onSelect={setPrompt} />
        </div>

        <div className="mb-4">
          <label
            htmlFor="ai-instruction"
            className="mb-2 block text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)]"
          >
            指示
          </label>
          <textarea
            id="ai-instruction"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            className="w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm focus:border-[var(--color-coral)] focus:outline-none"
          />
        </div>

        <Button onClick={generate} disabled={busy || !prompt.trim()} className="mb-4 w-full">
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              生成中…（10〜30秒）
            </>
          ) : (
            "生成"
          )}
        </Button>

        {error && <p className="mb-4 text-sm text-[var(--color-coral-dark)]">{error}</p>}

        {result && (
          <div className="flex flex-col gap-3">
            <div className="rounded-[10px] bg-[var(--color-coral-soft)]/30 p-3 text-sm text-[var(--color-ink)]">
              <div className="text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
                AI の説明
              </div>
              <div className="mt-1 font-medium">{result.ai_summary || "（説明なし）"}</div>
              <div className="mt-1 text-[11px] text-[var(--color-ink-muted)]">
                提案された操作: {result.planned_count} 件 / 適用可能: {result.operations_count} 件
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  onApply(result);
                  setResult(null);
                }}
                className="flex-1"
              >
                プレビューで比較
              </Button>
              <Button variant="secondary" onClick={() => setResult(null)} className="flex-1">
                やり直す
              </Button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
